import React, { PropsWithChildren } from 'react'
import { AuthProvider, useAuth } from 'react-oidc-context'
import { AuthPreloadRoutes, SessionRenewalBridge } from '../components'
import { useIsMFE } from '@/contexts/mfe-context'
import { getEnv } from '@/lib/config/runtime-env'
import { getAuthManager, initAuthManager } from '@/lib/auth/auth-manager'

/**
 * Auth config injected by the MF host at mount time. When present (embedded),
 * mmp points its OIDC client at the host's authority + client_id so it reads
 * the SAME localStorage session key the host wrote and reuses the single host
 * session instead of starting its own OIDC client. When absent (standalone),
 * mmp uses its own env config and logs in independently.
 */
export interface HostAuthConfig {
  authority: string
  client_id: string
}

interface AuthProps {
  children: React.ReactNode
  locale: string
  /** Host-provided OIDC config; presence means we're embedded. */
  hostAuth?: HostAuthConfig
}

function handleSigninCallback() {
  const isIframe = window.parent !== window
  const isOidcCallback =
    window.location.search.includes('code=') ||
    window.location.hash.includes('code=')

  if (isIframe && isOidcCallback) {
    // prettier-ignore
    window.parent.postMessage( // NOSONAR
      {
        type: "login-success",
      },
      "*",
    )
  }
}

export default function Auth({
  children,
  locale,
  hostAuth,
}: PropsWithChildren<AuthProps>) {
  const isMFE = useIsMFE()
  const env = getEnv()

  // Embedded → use the host's authority + client_id so we share its session key.
  // Standalone → fall back to this remote's own env config.
  const authority = hostAuth?.authority ?? env.IDENTITY_AUTHORITY_URL
  const clientId = hostAuth?.client_id ?? env.IDENTITY_CLIENT_ID

  // The IdP registers redirect URIs WITHOUT a trailing slash, and OIDC matches
  // redirect_uri by EXACT string. A stray trailing slash from env config (e.g.
  // "https://mmp-web-qc.gettameeni.com/") is rejected by the IdP ("invalid
  // redirect_uri" -> error page), so normalize it away defensively here.
  const redirectUri = env.IDENTITY_REDIRECT_URI?.replace(/\/+$/, '')

  // The manager is built once and reused; initAuthManager is idempotent, so a
  // re-render with the same config returns the existing instance.
  const userManager = React.useMemo(
    () =>
      initAuthManager({
        authority,
        clientId,
        clientSecret: env.CLIENT_SECRET,
        redirectUri,
        uiLocales: locale,
      }),
    [authority, clientId, env.CLIENT_SECRET, redirectUri, locale]
  )

  let content: React.ReactNode
  if (hostAuth) {
    // Embedded with host-provided config: the host owns renewal for the single
    // shared session, so no renewal bridge here — two renewers would rotate the
    // same one-time-use refresh token against each other.
    content = (
      <>
        <CrossAppSync authority={authority} clientId={clientId} />
        {children}
      </>
    )
  } else if (isMFE) {
    // Embedded without explicit hostAuth: this app runs its own session off its
    // own env config, so it owns renewal. Still skip our own preload routes.
    content = (
      <>
        <SessionRenewalBridge />
        {children}
      </>
    )
  } else {
    content = (
      <>
        <SessionRenewalBridge />
        <AuthPreloadRoutes>{children}</AuthPreloadRoutes>
      </>
    )
  }

  return (
    <AuthProvider
      userManager={userManager}
      onSigninCallback={handleSigninCallback}
    >
      {content}
    </AuthProvider>
  )
}

/** Typed reference to window.__HOST_AUTH__ exposed by TameeniWebsite/mf/auth.js */
interface HostAuthAPI {
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  getUser: () => Promise<unknown>
  onAuthChange: (cb: (user: unknown) => void) => () => void
}

function getHostAuth(): HostAuthAPI | undefined {
  return (window as Window & { __HOST_AUTH__?: HostAuthAPI }).__HOST_AUTH__
}

/**
 * Keeps react-oidc-context in sync with the host's auth state.
 *
 * react-oidc-context loads the user once on mount and does not watch
 * localStorage for external writes. Two sources can change auth state:
 *
 * 1. Same-tab: the host fires `mf:authchange` (CustomEvent) after its
 *    UserManager emits userLoaded/userUnloaded.
 * 2. Cross-tab: another tab writes/clears the shared localStorage key, which
 *    triggers the `storage` event here.
 */
function CrossAppSync({
  authority,
  clientId,
}: {
  authority?: string
  clientId?: string
}) {
  const auth = useAuth()

  /**
   * Adopt whatever the writer already stored instead of minting a new token.
   *
   * This used to call signinSilent(), which reacted to someone else's
   * SUCCESSFUL renewal by starting another one — every tab's renewal made every
   * other tab rotate, and each rotation invalidated the token the previous one
   * had just started using. getUser() re-reads the shared key, and the
   * raiseEvent flag is what makes react-oidc-context pick the new user up
   * (it only listens for userLoaded).
   */
  const adoptStoredUser = React.useCallback(() => {
    void getAuthManager()
      .getUser(true)
      .catch(() => {})
  }, [])

  // Same-tab: host dispatches mf:authchange after its own UserManager fires.
  React.useEffect(() => {
    const hostAuth = getHostAuth()
    if (!hostAuth) return

    const unsubscribe = hostAuth.onAuthChange((user) => {
      if (user) {
        adoptStoredUser()
      } else {
        void auth.removeUser()
      }
    })

    return unsubscribe
  }, [auth, adoptStoredUser])

  // Cross-tab: storage event fires when another tab writes/clears the key.
  React.useEffect(() => {
    const storageKey = `oidc.user:${authority}:${clientId}`
    const onStorage = (e: StorageEvent) => {
      if (e.key !== storageKey) return
      if (e.newValue === e.oldValue) return
      if (!e.newValue) {
        void auth.removeUser()
      } else {
        adoptStoredUser()
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [auth, authority, clientId, adoptStoredUser])

  return null
}
